import Button from '@app/components/Button';
import Card from '@app/components/card/Card';
import PageWithHeader from '@app/components/PageWithHeader';
import SVG from '@app/components/SVG';
import {
  SupplierRegistrationProcessInstructionType,
  SupplierRegistrationProcessInstructionTypesSubsets,
} from '@app/domain/commonSupplierRegistration';
import useAppStores from '@app/hooks/useAppStores';
import useInfraStores from '@app/hooks/useInfraStores';
import useMountEffect from '@app/hooks/useMountEffect';
import DownloadIcon from '@app/images/download-v2-accent-enabled.svg';
import { itemOrFirst } from '@app/utils/arrayUtils';
import browserHistory from '@app/utils/browserHistory';
import { EMAIL_REGEX, REDUCTED_EMAIL_REGEX } from '@app/utils/regexUtils';
import styled from '@emotion/styled';
import BackButton from '@supplierRegistration/components/BackButton';
import { SupplierRegistrationProgressStep } from '@supplierRegistration/domain/supplierRegistration';
import SupplierRegistrationMode from '@supplierRegistration/supplierRegistrationMode';
import { observer } from 'mobx-react';
import qs from 'query-string';
import React, { FC, ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { InstructionFormDetails } from '../commonAfterFormPage/InstructionFormDetails';
import { getInstructionFormDetails } from './getInstructionFormDetails';
import TransferInstructionForm from '../commonAfterFormPage/TransferInstructionForm';
import { ActionBadge, ActionBadgeGreen, PleaseNoteSection, TransferTypeTitle } from '../Styles';
import NoActionRequiredForm from './afterFormPage/NoActionRequiredForm';
import { EVENTS } from '@app/libs/analytics';
import useVendorPortalTrackEvent from '@supplierRegistration/hooks/useVendorPortalTrackEvent';

interface AfterFormPageQueryParams {
  email?: string | string[];
  instructionType?: SupplierRegistrationProcessInstructionType | SupplierRegistrationProcessInstructionType[];
}

const AfterFormPage: FC = observer(() => {
  const { t } = useTranslation();
  const location = useLocation();
  const { email: emailQueryParam, instructionType: instructionTypeQueryParam } = qs.parse(
    location.search,
  ) as AfterFormPageQueryParams;
  const { navigationStore } = useInfraStores<SupplierRegistrationMode>();
  const { supplierRegistrationFormStore, supplierRegistrationCustomerDataStore } = useAppStores<SupplierRegistrationMode>();
  const trackEvent = useVendorPortalTrackEvent();
  const isIpsMode = navigationStore.isIpsPage;

  const instructionType = instructionTypeQueryParam
    ? itemOrFirst(instructionTypeQueryParam)
    : SupplierRegistrationProcessInstructionType.bofaWireTransfer;
  const configuration = supplierRegistrationCustomerDataStore.currentConfiguration.result ?? null;
  const instructionTypeString = instructionType ? instructionType.toString() : '';
  const instructionsShownFired = useRef(false);

  useEffect(() => {
    if (instructionsShownFired.current || !configuration) return;
    instructionsShownFired.current = true;
    trackEvent({
      name: EVENTS.VENDOR_PORTAL_INSTRUCTIONS_SHOWN,
      ref_id: configuration.ref ?? undefined,
      instructions_type: instructionTypeString,
    });
  }, [configuration, instructionTypeString, trackEvent]);

  useMountEffect(() => {
    supplierRegistrationFormStore.clearSavedFormValues();
    supplierRegistrationFormStore.setProgressStep(SupplierRegistrationProgressStep.OneTimeTransfer);
  });

  const email = emailQueryParam ? itemOrFirst(emailQueryParam) : null;

  if (!email?.match(EMAIL_REGEX) && !email?.match(REDUCTED_EMAIL_REGEX)) {
    browserHistory.push(navigationStore.generateAccountValidationForSupplierWelcome(isIpsMode, null));
  }

  const instructionFormDetails = getInstructionFormDetails(instructionType);
  const isIpsInstruction = !!instructionType && SupplierRegistrationProcessInstructionTypesSubsets.ips.includes(instructionType);

  const createNotes = (instructionForm: InstructionFormDetails): ReactNode => {
    return instructionForm.pleaseNote.pleaseNoteItem.map((note) => {
      const noteText = t(note);
      const emailAddress = 'accountvalidation@nsknox.net';

      if (noteText.includes(emailAddress)) {
        const parts = noteText.split(emailAddress);
        return (
          <p key={note}>
            {parts[0]}
            <a href={`mailto:${emailAddress}`}>{emailAddress}</a>
            {parts[1]}
          </p>
        );
      }

      return <p key={note}>{noteText}</p>;
    });
  };

  const renderInvoice = (): ReactNode => {
    if (instructionFormDetails.pleaseNote.hideInvoice) {
      return null;
    }

    return (
      <p>
        {isIpsInstruction ? t('supplierValidation.afterRegister.ips.invoice') : t('supplierValidation.afterRegister.invoice')}
      </p>
    );
  };

  return (
    <PageWithHeader paddingTop={20} widthReactive>
      <BackButton
        onClick={(): void => {
          window.history.back();
        }}
      >
        {t('supplierValidation.supplierRegister.back')}
      </BackButton>
      <div role='status' data-testid='after-form-details'>
        {instructionType && SupplierRegistrationProcessInstructionTypesSubsets.bankValidation.includes(instructionType) ? (
          <MainCard>
            <ActionBadgeGreen>{t('supplierValidation.noActionRequired')}</ActionBadgeGreen>
            <TransferTypeTitle>{t('supplierValidation.knoxAccountValidation')}</TransferTypeTitle>
            <NoActionRequiredForm isIpsMode={true} />
          </MainCard>
        ) : (
          <>
            <MainCard>
              <ActionBadge>{t('supplierValidation.actionRequired')}</ActionBadge>
              <TransferTypeTitle>{t('supplierValidation.knoxAccountValidation')}</TransferTypeTitle>
              <TransferInstructionForm instructionDetails={instructionFormDetails} />
            </MainCard>
            <PleaseNoteSection>
              <p style={{ fontWeight: '600', fontSize: '20px' }} className='fw-600'>
                {t('supplierValidation.supplierRegister.pleaseNote')}
              </p>
              {createNotes(instructionFormDetails)}
              {renderInvoice()}
              <FAQLink
                id='vldt-for-supp-step-2-faq'
                data-testid='vldt-for-supp-step-2-faq'
                to={navigationStore.generateAccountValidationForSuppliersFAQ(
                  isIpsMode,
                  supplierRegistrationCustomerDataStore.currentReference?.referenceId ?? null,
                )}
              >
                {t('supplierValidation.faq')}
              </FAQLink>
            </PleaseNoteSection>
            <DownloadButtonContainer>
              <Button
                id='download-instructions-button'
                data-testid='download-instructions-button'
                className='w-fit flex items-center gap-2 ml-auto'
                colorScheme='primary'
                onClick={(): void => {
                  trackEvent({
                    name: EVENTS.VENDOR_PORTAL_INSTRUCTIONS_DOWNLOAD,
                    ref_id: configuration?.ref ?? undefined,
                    instructions_type: instructionTypeString,
                  });
                  window.open(instructionFormDetails.downloadLink, '_blank');
                }}
              >
                Download <SVG accessibilityLabel='' height={18} image={DownloadIcon} />
              </Button>
            </DownloadButtonContainer>
          </>
        )}
      </div>
    </PageWithHeader>
  );
});

export default AfterFormPage;

const MainCard = styled(Card)`
  font-weight: 200;
  max-width: 1000px;
  align-self: center;
  padding: 54px 40px;
  line-height: 1.4;
  margin-bottom: 52px;
  align-items: center;
  justify-content: center;
  display: flex;
  flex-direction: column;
`;

export const WireTransferWarningBox = styled.div`
  color: #b958ec;
  font-size: 14px;
  letter-spacing: 0;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.6;
  padding-top: 5px;
`;

export const WarningTitle = styled.div`
  font-size: 16px;
`;

export const FAQLink = styled(Link)`
  display: block;
  font-size: 14px;
  font-weight: normal;
  text-decoration: underline !important;
  margin-top: 16px;
`;

export const DownloadButtonContainer = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: end;
`;

export const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 16px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0056b3;
  }

  span {
    margin-right: 8px;
  }
`;
